import React from 'react';

/**
 * Opens a file preview in a popup window with custom controls
 * @param {string} fileURL - The URL of the file to preview
 * @param {string} fileName - The name of the file (optional)
 * @param {Object} options - Configuration options (optional)
 */
export const openFilePreview = (fileURL, fileName = 'Document', options = {}) => {
    // Convert relative URL to absolute so it resolves correctly inside the blank popup window
    if (fileURL && typeof fileURL === 'string' && fileURL.startsWith('/')) {
        fileURL = window.location.origin + fileURL;
    }
    // Default options
    const defaultOptions = {
        width: 1200,
        height: 800,
        maxWidthPercent: 0.85,
        maxHeightPercent: 0.85,
        showDownloadButton: true,
        showMaximizeButton: true,
        showRestoreButton: true,
        customTitle: null,
        onDownload: null, // Custom download handler
        onClose: null     // Custom close handler
    };

    const config = { ...defaultOptions, ...options };

    // Improved file type detection
    let ext = '';
    
    // First try to get extension from the file URL
    if (fileURL && typeof fileURL === 'string') {
        const urlParts = fileURL.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        const fileNameFromUrl = lastPart.split('?')[0];
        
        if (fileNameFromUrl.includes('.')) {
            ext = fileNameFromUrl.split('.').pop().toLowerCase();
        }
    }
    
    // Fallback: try to get extension from fileName parameter
    if (!ext && fileName && typeof fileName === 'string' && fileName.includes('.')) {
        ext = fileName.split('.').pop().toLowerCase();
    }
    
    // If still no extension, try to detect from URL patterns
    if (!ext && fileURL) {
        const urlLower = fileURL.toLowerCase();
        if (urlLower.includes('.pdf')) ext = 'pdf';
        else if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) ext = 'jpg';
        else if (urlLower.includes('.png')) ext = 'png';
        else if (urlLower.includes('.gif')) ext = 'gif';
        else if (urlLower.includes('.doc')) ext = 'doc';
        else if (urlLower.includes('.docx')) ext = 'docx';
        else if (urlLower.includes('.mp4')) ext = 'mp4';
        else if (urlLower.includes('.webm')) ext = 'webm';
        else if (urlLower.includes('.ogg') || urlLower.includes('.ogv')) ext = 'ogv';
        else if (urlLower.includes('.mov')) ext = 'mov';
        else if (urlLower.includes('.avi')) ext = 'avi';
        else if (urlLower.includes('.mkv')) ext = 'mkv';
    }

    console.log('File preview debug:', { fileURL, fileName, detectedExt: ext });

    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const pdfTypes = ['pdf'];
    const videoTypes = ['mp4', 'webm', 'ogv', 'ogg', 'mov', 'avi', 'mkv'];
    const videoMimeMap = { mp4: 'video/mp4', webm: 'video/webm', ogv: 'video/ogg', ogg: 'video/ogg', mov: 'video/mp4', avi: 'video/x-msvideo', mkv: 'video/x-matroska' };

    // Calculate popup window size and position
    const screenWidth = window.screen.availWidth;
    const screenHeight = window.screen.availHeight;
    const windowWidth = Math.min(config.width, screenWidth * config.maxWidthPercent);
    const windowHeight = Math.min(config.height, screenHeight * config.maxHeightPercent);
    const left = (screenWidth - windowWidth) / 2;
    const top = Math.max(0, (screenHeight - windowHeight) / 2 - 50);

    // Enhanced window features
    const windowFeatures = [
        `width=${windowWidth}`,
        `height=${windowHeight}`,
        `left=${left}`,
        `top=${top}`,
        'resizable=yes',
        'scrollbars=no',
        'status=no',
        'menubar=no',
        'toolbar=no',
        'location=no',
        'directories=no',
        'personalbar=no',
        'copyhistory=no',
        'dependent=yes'
    ].join(',');

    // Open new popup window
    const previewWindow = window.open('', `preview_${Date.now()}`, windowFeatures);
    
    if (!previewWindow) {
        alert('Popup blocked! Please allow popups for this site to view documents.');
        return null;
    }

    // Create the HTML content for the preview window
    const displayFileName = fileName || 'Document';
    const displayExt = ext ? `.${ext}` : '';
    const windowTitle = config.customTitle || `Preview: ${displayFileName}`;

    // Enhanced styles
    const commonStyles = `
        <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                background: #ffffff;
                display: flex;
                flex-direction: column;
                height: 100vh;
                overflow: hidden;
                margin: 0 !important;
                padding: 0 !important;
            }
            
            .custom-header {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                padding: 8px 16px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                min-height: 45px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.15);
                user-select: none;
                cursor: move;
                position: relative;
                z-index: 1000;
            }
            
            .window-title { 
                font-size: 14px; 
                font-weight: 600; 
                display: flex;
                align-items: center;
                gap: 8px;
                flex: 1;
            }
            
            .file-icon {
                width: 20px;
                height: 20px;
                background: rgba(255,255,255,0.25);
                border-radius: 3px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 10px;
                font-weight: bold;
                letter-spacing: -0.5px;
            }
            
            .window-controls { 
                display: flex; 
                gap: 6px; 
                align-items: center;
            }
            
            .window-btn {
                width: 30px;
                height: 30px;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.15s ease;
                background: rgba(255,255,255,0.1);
                color: white;
                position: relative;
                font-size: 0;
            }
            
            .window-btn:hover {
                background: rgba(255,255,255,0.2);
                transform: scale(1.05);
            }
            
            .window-btn:active {
                transform: scale(0.95);
            }
            
            .btn-download { background: rgba(40,167,69,0.9); }
            .btn-download:hover { background: rgba(40,167,69,1); }
            
            .btn-restore { background: rgba(108,117,125,0.9); }
            .btn-restore:hover { background: rgba(108,117,125,1); }
            
            .btn-maximize { background: rgba(0,123,255,0.9); }
            .btn-maximize:hover { background: rgba(0,123,255,1); }
            
            .btn-close { background: rgba(220,53,69,0.9); }
            .btn-close:hover { background: rgba(220,53,69,1); }
            
            .window-btn::after {
                content: attr(data-tooltip);
                position: absolute;
                top: 35px;
                left: 50%;
                transform: translateX(-50%);
                background: rgba(0,0,0,0.9);
                color: white;
                padding: 4px 8px;
                border-radius: 3px;
                font-size: 11px;
                white-space: nowrap;
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.2s ease;
                z-index: 1001;
            }
            .window-btn:hover::after {
                opacity: 1;
            }
            
            .content {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                overflow: hidden;
                background: #f8f9fa;
                position: relative;
            }
            
            .image {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
                box-shadow: 0 4px 20px rgba(0,0,0,0.1);
                border-radius: 4px;
            }
            
            .pdf-frame {
                width: 100%;
                height: 100%;
                border: none;
                background: white;
            }
            
            .download-area {
                text-align: center;
                padding: 40px;
                background: white;
                border-radius: 8px;
                margin: 20px;
                box-shadow: 0 4px 20px rgba(0,0,0,0.1);
                max-width: 400px;
            }
            
            .download-area h2 {
                color: #495057;
                margin-bottom: 10px;
                font-size: 20px;
            }
            
            .download-area p {
                color: #6c757d;
                margin-bottom: 25px;
                font-size: 14px;
            }
            
            .download-btn-large {
                padding: 12px 24px;
                background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
                color: white;
                text-decoration: none;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 600;
                display: inline-flex;
                align-items: center;
                gap: 8px;
                transition: all 0.3s ease;
                box-shadow: 0 4px 15px rgba(40,167,69,0.3);
            }
            
            .download-btn-large:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(40,167,69,0.4);
                color: white;
                text-decoration: none;
            }
            
            .loading, .error {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                color: #6c757d;
            }
        </style>
    `;

    // Generate control buttons HTML
    const generateControlButtons = () => {
        let buttons = '';
        
        if (config.showDownloadButton) {
            buttons += `
                <button class="window-btn btn-download" onclick="downloadFile()" data-tooltip="Download">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                    </svg>
                </button>
            `;
        }
        
        if (config.showRestoreButton) {
            buttons += `
                <button class="window-btn btn-restore" onclick="restoreWindow()" data-tooltip="Restore" style="display:none;" id="restoreBtn">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M4 8h4V4h8v4h4v12H4V8zm6-2v2h4V6h-4z"/>
                    </svg>
                </button>
            `;
        }
        
        if (config.showMaximizeButton) {
            buttons += `
                <button class="window-btn btn-maximize" onclick="maximizeWindow()" data-tooltip="Maximize" id="maximizeBtn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                    </svg>
                </button>
            `;
        }
        
        buttons += `
            <button class="window-btn btn-close" onclick="closeWindow()" data-tooltip="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
            </button>
        `;
        
        return buttons;
    };

    // Generate JavaScript functions
    const generateJavaScript = () => {
        return `
            <script>
                let isMaximized = false;
                let originalState = {
                    width: window.outerWidth,
                    height: window.outerHeight,
                    left: window.screenX,
                    top: window.screenY
                };
                
                function hideLoading() {
                    const loading = document.getElementById('loading');
                    if (loading) loading.style.display = 'none';
                }
                
                function downloadFile() {
                    ${config.onDownload ? 
                        `window.opener.postMessage({type: 'download', fileURL: '${fileURL}', fileName: '${displayFileName}'}, '*');` :
                        `const a = document.createElement('a');
                         a.href = '${fileURL}';
                         a.download = '${displayFileName}';
                         document.body.appendChild(a);
                         a.click();
                         document.body.removeChild(a);`
                    }
                }
                
                function maximizeWindow() {
                    originalState = {
                        width: window.outerWidth,
                        height: window.outerHeight,
                        left: window.screenX,
                        top: window.screenY
                    };
                    window.resizeTo(screen.availWidth, screen.availHeight);
                    window.moveTo(0, 0);
                    const maximizeBtn = document.getElementById('maximizeBtn');
                    const restoreBtn = document.getElementById('restoreBtn');
                    if (maximizeBtn) maximizeBtn.style.display = 'none';
                    if (restoreBtn) restoreBtn.style.display = 'flex';
                    isMaximized = true;
                }
                
                function restoreWindow() {
                    window.resizeTo(originalState.width, originalState.height);
                    window.moveTo(originalState.left, originalState.top);
                    const maximizeBtn = document.getElementById('maximizeBtn');
                    const restoreBtn = document.getElementById('restoreBtn');
                    if (maximizeBtn) maximizeBtn.style.display = 'flex';
                    if (restoreBtn) restoreBtn.style.display = 'none';
                    isMaximized = false;
                }
                
                function closeWindow() {
                    ${config.onClose ? 
                        `window.opener.postMessage({type: 'close'}, '*');` : 
                        ''
                    }
                    window.close();
                }
                
                function showError() {
                    hideLoading();
                    document.querySelector('.content').innerHTML = '<div class="error"><h2>Error Loading File</h2><p>Unable to display this file.</p></div>';
                }
                
                // Drag functionality
                let isDragging = false;
                let dragOffset = { x: 0, y: 0 };
                
                function startDrag(e) {
                    if (isMaximized) return;
                    isDragging = true;
                    dragOffset.x = e.clientX - window.screenX;
                    dragOffset.y = e.clientY - window.screenY;
                    document.addEventListener('mousemove', drag);
                    document.addEventListener('mouseup', stopDrag);
                    e.preventDefault();
                }
                
                function drag(e) {
                    if (!isDragging) return;
                    window.moveTo(e.screenX - dragOffset.x, e.screenY - dragOffset.y);
                }
                
                function stopDrag() {
                    isDragging = false;
                    document.removeEventListener('mousemove', drag);
                    document.removeEventListener('mouseup', stopDrag);
                }
                
                window.focus();
                setTimeout(hideLoading, 3000);
            </script>
        `;
    };

    let htmlContent = '';

    if (imageTypes.includes(ext)) {
        htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${windowTitle}</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <link rel="icon" href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><text y='20' font-size='20'>🖼️</text></svg>">
                ${commonStyles}
            </head>
            <body>
                <div class="custom-header" onmousedown="startDrag(event)">
                    <div class="window-title">
                        <div class="file-icon">IMG</div>
                        <span>${windowTitle}</span>
                    </div>
                    <div class="window-controls">
                        ${generateControlButtons()}
                    </div>
                </div>
                <div class="content">
                    <img src="${fileURL}" alt="Preview" class="image" 
                        onerror="showError()" onload="hideLoading()" />
                    <div class="loading" id="loading">Loading image...</div>
                </div>
                ${generateJavaScript()}
            </body>
            </html>
        `;
    } else if (pdfTypes.includes(ext)) {
        htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${windowTitle}</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <link rel="icon" href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><text y='20' font-size='20'>📄</text></svg>">
                ${commonStyles}
            </head>
            <body>
                <div class="custom-header" onmousedown="startDrag(event)">
                    <div class="window-title">
                        <div class="file-icon">PDF</div>
                        <span>${windowTitle}</span>
                    </div>
                    <div class="window-controls">
                        ${generateControlButtons()}
                    </div>
                </div>
                <div class="content">
                    <iframe src="${fileURL}" class="pdf-frame" 
                            onerror="showError()" onload="hideLoading()"></iframe>
                    <div class="loading" id="loading">Loading PDF...</div>
                </div>
                ${generateJavaScript()}
            </body>
            </html>
        `;
    } else if (videoTypes.includes(ext)) {
        const videoMime = videoMimeMap[ext] || 'video/mp4';
        htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${windowTitle}</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <link rel="icon" href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><text y='20' font-size='20'>🎬</text></svg>">
                ${commonStyles}
                <style>
                    .video-player {
                        max-width: 100%;
                        max-height: 100%;
                        outline: none;
                        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
                        border-radius: 4px;
                        background: #000;
                    }
                </style>
            </head>
            <body>
                <div class="custom-header" onmousedown="startDrag(event)">
                    <div class="window-title">
                        <div class="file-icon">VID</div>
                        <span>${windowTitle}</span>
                    </div>
                    <div class="window-controls">
                        ${generateControlButtons()}
                    </div>
                </div>
                <div class="content">
                    <video class="video-player" controls autoplay
                           onerror="showError()" oncanplay="hideLoading()">
                        <source src="${fileURL}" type="${videoMime}">
                        Your browser does not support the video tag.
                    </video>
                    <div class="loading" id="loading">Loading video...</div>
                </div>
                ${generateJavaScript()}
            </body>
            </html>
        `;
    } else {
        htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Download: ${displayFileName}</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <link rel="icon" href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><text y='20' font-size='20'>📎</text></svg>">
                ${commonStyles}
            </head>
            <body>
                <div class="custom-header" onmousedown="startDrag(event)">
                    <div class="window-title">
                        <div class="file-icon">${ext.toUpperCase().substring(0,3)}</div>
                        <span>Download: ${displayFileName}</span>
                    </div>
                    <div class="window-controls">
                        ${generateControlButtons()}
                    </div>
                </div>
                <div class="content">
                    <div class="download-area">
                        <h2>Preview not available</h2>
                        <p>Preview is not available for this file type${displayExt ? ` (${displayExt})` : ''}</p>
                        <a href="${fileURL}" download="${displayFileName}" class="download-btn-large" onclick="downloadFile()">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                            </svg>
                            Download ${displayFileName}
                        </a>
                    </div>
                </div>
                ${generateJavaScript()}
            </body>
            </html>
        `;
    }

    // Write the HTML content to the popup window
    previewWindow.document.write(htmlContent);
    previewWindow.document.close();
    previewWindow.focus();

    return previewWindow;
};

const FilePreviewModal = ({ fileUrl, fileName, onClose }) => {
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [size, setSize] = React.useState({ width: 1000, height: 800 });
    const [isDragging, setIsDragging] = React.useState(false);
    const [isResizing, setIsResizing] = React.useState(false);
    const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });
    const modalRef = React.useRef(null);

    React.useEffect(() => {
        // Center the modal on mount
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        setPosition({
            x: (windowWidth - size.width) / 2,
            y: (windowHeight - size.height) / 2
        });
    }, []); // Run once on mount

    const handleMouseDown = (e) => {
        if (e.target.closest('.modal-header-actions')) return;
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const handleMouseMove = React.useCallback((e) => {
        if (isDragging) {
            setPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        }
        if (isResizing) {
            setSize({
                width: Math.max(400, e.clientX - position.x),
                height: Math.max(300, e.clientY - position.y)
            });
        }
    }, [isDragging, isResizing, dragOffset, position]);

    const handleMouseUp = () => {
        setIsDragging(false);
        setIsResizing(false);
    };

    React.useEffect(() => {
        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, handleMouseMove]);

    if (!fileUrl) return null;

    return (
        <div 
            className="modal-overlay" 
            style={{ 
                position: 'fixed', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '100%', 
                zIndex: 1000,
                pointerEvents: 'none' // Allow clicks to pass through overlay to underlying elements if needed, but here we want to block interactions? usually modal blocks.
                // Actually, if we want it to be a "window" that floats, we might not want a blocking overlay. 
                // But typically modals block. Let's keep it simple: blocking overlay but transparent so it looks like a window
            }}
        >
             {/* Blocking transparent background to catch clicks outside if we want to close? Or just a container layer. 
                 If we want it "draggable", usually we don't dim the background or block interaction if it's a "palette". 
                 But for a "Preview Modal", blocking is standard. 
                 Let's make the background semi-transparent black to indicate modal state.
             */}
            <div style={{ position: 'fixed', top:0, left:0, right:0, bottom:0, backgroundColor: 'rgba(0,0,0,0.5)', pointerEvents: 'auto' }} onClick={onClose}></div>

            <div 
                ref={modalRef}
                className="modal-content" 
                style={{ 
                    position: 'absolute', 
                    left: `${position.x}px`, 
                    top: `${position.y}px`, 
                    width: `${size.width}px`, 
                    height: `${size.height}px`, 
                    backgroundColor: '#fff', 
                    borderRadius: '8px', 
                    boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                    overflow: 'hidden', 
                    display: 'flex', 
                    flexDirection: 'column',
                    pointerEvents: 'auto'
                }}
            >
                <div 
                    className="modal-header"
                    onMouseDown={handleMouseDown}
                    style={{ 
                        padding: '10px 20px', 
                        borderBottom: '1px solid #ddd', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        backgroundColor: '#f8f9fa',
                        cursor: 'move',
                        userSelect: 'none' 
                    }}
                >
                    <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                         <i className="fa fa-file-pdf" style={{ marginRight: '10px', color: '#dc3545' }}></i>
                        {fileName || 'Document Preview'}
                    </h3>
                    <div className="modal-header-actions">
                        <button 
                            onClick={onClose}
                            style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
                        >
                            &times;
                        </button>
                    </div>
                </div>
                <div style={{ flex: 1, padding: 0, overflow: 'hidden', position: 'relative', backgroundColor: '#525659' }}>
                    <iframe 
                        src={fileUrl} 
                        style={{ width: '100%', height: '100%', border: 'none' }} 
                        title="File Preview"
                    />
                </div>
                
                {/* Resize Handle */}
                <div 
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        setIsResizing(true);
                    }}
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: '20px',
                        height: '20px',
                        cursor: 'se-resize',
                        zIndex: 10
                    }}
                >
                    <svg viewBox="0 0 24 24" style={{ width: '100%', height: '100%', fill: '#6c757d' }}>
                        <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM18 18H16V16H18V18Z" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default FilePreviewModal;